import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

// ============================================
// TYPES
// ============================================

export interface CalendarSource {
  id: string;
  name: string;
  type: 'crm' | 'google' | 'sales_activities' | 'project_tasks';
  color: string;
  enabled: boolean;
  googleCalendarId?: string;
}

interface CalendarModeContextType {
  // Calendar mode state
  isCalendarMode: boolean;
  setCalendarMode: (enabled: boolean) => void;

  // Calendar sources (passed from CalendarPage)
  calendarSources: CalendarSource[];
  setCalendarSources: (sources: CalendarSource[]) => void;
  onCalendarSourceToggle: ((id: string) => void) | null;
  setOnCalendarSourceToggle: (handler: ((id: string) => void) | null) => void;

  // Refresh handler (passed from CalendarPage)
  onRefreshCalendar: (() => void) | null;
  setOnRefreshCalendar: (handler: (() => void) | null) => void;

  // Google loading state
  googleLoading: boolean;
  setGoogleLoading: (loading: boolean) => void;

  // Google error state (to show "Connect Google" prompt)
  googleError: string | null;
  setGoogleError: (error: string | null) => void;

  // Microsoft (Outlook) loading + refresh (speculare a Google)
  microsoftLoading: boolean;
  setMicrosoftLoading: (loading: boolean) => void;
  onRefreshMicrosoftCalendar: (() => void) | null;
  setOnRefreshMicrosoftCalendar: (handler: (() => void) | null) => void;

  // Open profile modal handler (to connect Google account)
  onOpenProfileModal: (() => void) | null;
  setOnOpenProfileModal: (handler: (() => void) | null) => void;

  // Can access deals (for showing sales activities section)
  canAccessDeals: boolean;
  setCanAccessDeals: (canAccess: boolean) => void;

  // Can view bookings (for showing bookings section)
  canViewBooking: boolean;
  setCanViewBooking: (canView: boolean) => void;

  // Booking status filters
  bookingStatusFilters: string[];
  onBookingStatusFilterToggle: ((status: string) => void) | null;
  setOnBookingStatusFilterToggle: (handler: ((status: string) => void) | null) => void;
  setBookingStatusFilters: (filters: string[]) => void;

  // AlfaDocs integration active
  alfadocsActive: boolean;
  setAlfadocsActive: (active: boolean) => void;

  // AlfaDocs studio filters (org multi-connessione)
  alfadocsStudios: { practiceId: string; name: string }[];
  setAlfadocsStudios: (studios: { practiceId: string; name: string }[]) => void;
  alfadocsStudioFilters: string[] | null;
  setAlfadocsStudioFilters: (filters: string[] | null) => void;
  onAlfadocsStudioFilterToggle: ((practiceId: string) => void) | null;
  setOnAlfadocsStudioFilterToggle: (handler: ((practiceId: string) => void) | null) => void;

  // Translations
  translations: Record<string, string>;
  setTranslations: (t: Record<string, string>) => void;

  // Mobile drawer state
  mobileCalendarDrawerOpen: boolean;
  setMobileCalendarDrawerOpen: (open: boolean) => void;

  // Mini calendar date navigation
  currentCalendarDate: Date;
  setCurrentCalendarDate: (date: Date) => void;
  onDateSelect: ((date: Date) => void) | null;
  setOnDateSelect: (handler: ((date: Date) => void) | null) => void;
}

// ============================================
// CONTEXT
// ============================================

const CalendarModeContext = createContext<CalendarModeContextType | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

interface CalendarModeProviderProps {
  children: ReactNode;
}

export function CalendarModeProvider({ children }: CalendarModeProviderProps) {
  const [isCalendarMode, setIsCalendarMode] = useState(false);
  const [calendarSources, setCalendarSources] = useState<CalendarSource[]>([]);
  const [onCalendarSourceToggle, setOnCalendarSourceToggleState] = useState<((id: string) => void) | null>(null);
  const [onRefreshCalendar, setOnRefreshCalendarState] = useState<(() => void) | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [onRefreshMicrosoftCalendar, setOnRefreshMicrosoftCalendarState] = useState<(() => void) | null>(null);
  const setOnRefreshMicrosoftCalendar = useCallback((handler: (() => void) | null) => {
    setOnRefreshMicrosoftCalendarState(() => handler);
  }, []);
  const [onOpenProfileModal, setOnOpenProfileModalState] = useState<(() => void) | null>(null);
  const [canAccessDeals, setCanAccessDeals] = useState(false);
  const [canViewBooking, setCanViewBooking] = useState(true);
  const [bookingStatusFilters, setBookingStatusFilters] = useState<string[]>(['confirmed', 'pending']);
  const [onBookingStatusFilterToggle, setOnBookingStatusFilterToggleState] = useState<((status: string) => void) | null>(null);
  const setOnBookingStatusFilterToggle = useCallback((handler: ((status: string) => void) | null) => {
    setOnBookingStatusFilterToggleState(() => handler);
  }, []);
  const [alfadocsActive, setAlfadocsActive] = useState(false);
  const [alfadocsStudios, setAlfadocsStudios] = useState<{ practiceId: string; name: string }[]>([]);
  const [alfadocsStudioFilters, setAlfadocsStudioFilters] = useState<string[] | null>(null);
  const [onAlfadocsStudioFilterToggle, setOnAlfadocsStudioFilterToggleState] = useState<((practiceId: string) => void) | null>(null);
  const setOnAlfadocsStudioFilterToggle = useCallback((handler: ((practiceId: string) => void) | null) => {
    setOnAlfadocsStudioFilterToggleState(() => handler);
  }, []);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [mobileCalendarDrawerOpen, setMobileCalendarDrawerOpen] = useState(false);
  const [currentCalendarDate, setCurrentCalendarDate] = useState<Date>(new Date());
  const [onDateSelect, setOnDateSelectState] = useState<((date: Date) => void) | null>(null);

  const setCalendarMode = useCallback((enabled: boolean) => {
    setIsCalendarMode(enabled);
    // Quando si esce dal calendar mode, chiudi il drawer mobile
    if (!enabled) {
      setMobileCalendarDrawerOpen(false);
    }
  }, []);

  // Wrapper per setOnCalendarSourceToggle che accetta una funzione
  const setOnCalendarSourceToggle = useCallback((handler: ((id: string) => void) | null) => {
    setOnCalendarSourceToggleState(() => handler);
  }, []);

  // Wrapper per setOnRefreshCalendar che accetta una funzione
  const setOnRefreshCalendar = useCallback((handler: (() => void) | null) => {
    setOnRefreshCalendarState(() => handler);
  }, []);

  // Wrapper per setOnDateSelect che accetta una funzione
  const setOnDateSelect = useCallback((handler: ((date: Date) => void) | null) => {
    setOnDateSelectState(() => handler);
  }, []);

  // Wrapper per setOnOpenProfileModal che accetta una funzione
  const setOnOpenProfileModal = useCallback((handler: (() => void) | null) => {
    setOnOpenProfileModalState(() => handler);
  }, []);

  const value: CalendarModeContextType = useMemo(() => ({
    isCalendarMode,
    setCalendarMode,
    calendarSources,
    setCalendarSources,
    onCalendarSourceToggle,
    setOnCalendarSourceToggle,
    onRefreshCalendar,
    setOnRefreshCalendar,
    googleLoading,
    setGoogleLoading,
    googleError,
    setGoogleError,
    microsoftLoading,
    setMicrosoftLoading,
    onRefreshMicrosoftCalendar,
    setOnRefreshMicrosoftCalendar,
    onOpenProfileModal,
    setOnOpenProfileModal,
    canAccessDeals,
    setCanAccessDeals,
    canViewBooking,
    setCanViewBooking,
    bookingStatusFilters,
    onBookingStatusFilterToggle,
    setOnBookingStatusFilterToggle,
    setBookingStatusFilters,
    alfadocsActive,
    setAlfadocsActive,
    alfadocsStudios,
    setAlfadocsStudios,
    alfadocsStudioFilters,
    setAlfadocsStudioFilters,
    onAlfadocsStudioFilterToggle,
    setOnAlfadocsStudioFilterToggle,
    translations,
    setTranslations,
    mobileCalendarDrawerOpen,
    setMobileCalendarDrawerOpen,
    currentCalendarDate,
    setCurrentCalendarDate,
    onDateSelect,
    setOnDateSelect,
  }), [
    isCalendarMode,
    setCalendarMode,
    calendarSources,
    onCalendarSourceToggle,
    setOnCalendarSourceToggle,
    onRefreshCalendar,
    setOnRefreshCalendar,
    googleLoading,
    googleError,
    microsoftLoading,
    onRefreshMicrosoftCalendar,
    setOnRefreshMicrosoftCalendar,
    onOpenProfileModal,
    setOnOpenProfileModal,
    canAccessDeals,
    canViewBooking,
    bookingStatusFilters,
    onBookingStatusFilterToggle,
    alfadocsActive,
    alfadocsStudios,
    alfadocsStudioFilters,
    onAlfadocsStudioFilterToggle,
    setOnAlfadocsStudioFilterToggle,
    translations,
    mobileCalendarDrawerOpen,
    currentCalendarDate,
    onDateSelect,
    setOnDateSelect,
  ]);

  return (
    <CalendarModeContext.Provider value={value}>
      {children}
    </CalendarModeContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

export function useCalendarMode() {
  const context = useContext(CalendarModeContext);
  if (context === undefined) {
    throw new Error('useCalendarMode must be used within a CalendarModeProvider');
  }
  return context;
}

/**
 * Safe version of useCalendarMode that returns default values if no provider is present.
 * Useful for components that may be rendered both inside and outside of CalendarModeProvider.
 */
export function useCalendarModeSafe(): CalendarModeContextType | null {
  const context = useContext(CalendarModeContext);
  return context ?? null;
}
